// v3.15-1081: Lightweight task quick-view modal for the Calendar page.
// Opens when a user clicks a task block on Day/Week/Month view.
//
// Shows:
//  - Title, client name, assignees, due date/time, status
//  - "Complete task" button (any assignee may complete — backend authoritative)
//  - "Open Deal" link to the client detail page
//
// Stabilization-safe: no payroll/ownership/funding/SMS touched.
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { CheckCircle, ExternalLink, Loader2, Clipboard, CalendarClock, X as XIcon } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { useNavigate } from 'react-router-dom';

export const TaskQuickViewModal = ({ open, onOpenChange, task, onUpdated }) => {
  const navigate = useNavigate();
  const [completing, setCompleting] = useState(false);
  // v3.15-1082: inline reschedule controls.
  const [rescheduling, setRescheduling] = useState(false);
  const [savingResched, setSavingResched] = useState(false);
  const [reDate, setReDate] = useState('');
  const [reTime, setReTime] = useState('');

  // When the modal (re)opens with a new task, seed the reschedule inputs
  // with the existing due values. React's recommended "reset state when a
  // prop changes" pattern — guarded by a tracked previous-key so it only
  // fires on transitions, not on every render.
  // https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes
  const taskKey = open && task ? task.task_id : null;
  const [prevTaskKey, setPrevTaskKey] = useState(null);
  if (taskKey !== prevTaskKey) {
    setPrevTaskKey(taskKey);
    setRescheduling(false);
    if (taskKey && task) {
      setReDate((task.due_date || '').slice(0, 10));
      setReTime(task.due_time || '');
    } else {
      setReDate('');
      setReTime('');
    }
  }

  if (!task) return null;

  const isCompleted = task.status === 'completed';
  const dueDateLabel = (() => {
    try {
      const d = new Date(task.date_time_start);
      const datePart = d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
      const timePart = task.due_time
        ? d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
        : '';
      return timePart ? `${datePart} · ${timePart}` : datePart;
    } catch {
      return '';
    }
  })();

  const completedLabel = (() => {
    if (!task.completed_at) return '';
    try {
      return new Date(task.completed_at).toLocaleString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric',
        hour: 'numeric', minute: '2-digit', hour12: true,
      });
    } catch {
      return task.completed_at;
    }
  })();

  const handleComplete = async () => {
    if (!task.task_id) return;
    setCompleting(true);
    try {
      await api.put(`/tasks/${task.task_id}`, { status: 'completed' });
      toast.success('Task completed');
      // v3.21-1788 · Ghost live-refresh.
      try { window.dispatchEvent(new CustomEvent('ghost:refresh', { detail: { reason: 'task_completed' } })); } catch (_e) { /* noop */ }
      onUpdated?.({ ...task, status: 'completed' });
      onOpenChange?.(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to complete task');
    } finally {
      setCompleting(false);
    }
  };

  const openDeal = () => {
    if (!task.client_id) {
      toast.error('No linked deal');
      return;
    }
    onOpenChange?.(false);
    navigate(`/clients/${task.client_id}`);
  };

  // v3.15-1082: inline reschedule via PUT /tasks/{id} with new due_date/due_time.
  const handleReschedule = async () => {
    if (!task.task_id) return;
    if (!reDate) {
      toast.error('Pick a date');
      return;
    }
    setSavingResched(true);
    try {
      const payload = {
        due_date: `${reDate}T${reTime || '00:00'}:00`,
        due_time: reTime || null,
      };
      await api.put(`/tasks/${task.task_id}`, payload);
      toast.success('Task rescheduled');
      // v3.21-1788 · Ghost live-refresh.
      try { window.dispatchEvent(new CustomEvent('ghost:refresh', { detail: { reason: 'task_rescheduled' } })); } catch (_e) { /* noop */ }
      onUpdated?.({
        ...task,
        due_date: payload.due_date,
        due_time: reTime || null,
        date_time_start: payload.due_date,
      });
      setRescheduling(false);
      onOpenChange?.(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to reschedule');
    } finally {
      setSavingResched(false);
    }
  };

  return (
    <Dialog open={!!open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="task-quick-view-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Clipboard className="h-4 w-4 text-purple-600" />
            <span data-testid="task-quick-view-title">{task.title || 'Task'}</span>
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          {task.client_name && (
            <div className="flex items-baseline gap-2">
              <span className="text-[11px] uppercase tracking-wide text-slate-500 w-24">Client</span>
              <span className="text-slate-800 font-medium" data-testid="task-quick-view-client">{task.client_name}</span>
            </div>
          )}
          <div className="flex items-baseline gap-2">
            <span className="text-[11px] uppercase tracking-wide text-slate-500 w-24">Due</span>
            <span className="text-slate-800" data-testid="task-quick-view-due">{dueDateLabel || '—'}</span>
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-[11px] uppercase tracking-wide text-slate-500 w-24">Status</span>
            <Badge
              variant={isCompleted ? 'secondary' : 'outline'}
              className={isCompleted ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-50 text-amber-700 border-amber-200'}
              data-testid="task-quick-view-status"
            >
              {isCompleted ? 'Completed' : task.status}
            </Badge>
          </div>
          {task.assigned_users_names?.length > 0 && (
            <div className="flex items-baseline gap-2">
              <span className="text-[11px] uppercase tracking-wide text-slate-500 w-24 pt-1">Assigned To</span>
              <div className="flex flex-wrap gap-1.5" data-testid="task-quick-view-assignees">
                {task.assigned_users_names.map((n, i) => (
                  <span
                    key={`tq-a-${i}`}
                    className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 text-[11px]"
                  >
                    {n}
                  </span>
                ))}
              </div>
            </div>
          )}
          {isCompleted && task.completed_by_name && (
            <div
              className="rounded bg-emerald-50 border border-emerald-200 px-2.5 py-1.5 text-[11px] text-emerald-800"
              data-testid="task-quick-view-completed-by"
            >
              Completed by <span className="font-semibold">{task.completed_by_name}</span>
              {completedLabel ? ` on ${completedLabel}` : ''}
            </div>
          )}
          {task.description && (
            <div>
              <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">Notes</div>
              <div className="text-slate-700 text-[12px] whitespace-pre-wrap">{task.description}</div>
            </div>
          )}

          {/* v3.15-1082: inline reschedule. Hidden by default; opens via the
              Reschedule button in the footer. Hidden when the task is already
              completed (no operational reason to move a completed task). */}
          {rescheduling && !isCompleted && (
            <div
              className="rounded border border-purple-200 bg-purple-50 p-3 space-y-2"
              data-testid="task-quick-view-reschedule-panel"
            >
              <div className="flex items-center justify-between">
                <div className="text-[11px] uppercase tracking-wide text-purple-800 font-semibold flex items-center gap-1">
                  <CalendarClock className="h-3 w-3" /> Reschedule task
                </div>
                <button
                  type="button"
                  onClick={() => setRescheduling(false)}
                  className="text-purple-700 hover:text-purple-900"
                  aria-label="Cancel reschedule"
                  data-testid="task-quick-view-reschedule-cancel"
                >
                  <XIcon className="h-3.5 w-3.5" />
                </button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-[10px] uppercase tracking-wide text-slate-500">Date</Label>
                  <Input
                    type="date"
                    value={reDate}
                    onChange={(e) => setReDate(e.target.value)}
                    className="h-8 text-xs"
                    data-testid="task-quick-view-reschedule-date"
                  />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-wide text-slate-500">Time</Label>
                  <Input
                    type="time"
                    value={reTime}
                    onChange={(e) => setReTime(e.target.value)}
                    className="h-8 text-xs"
                    data-testid="task-quick-view-reschedule-time"
                  />
                </div>
              </div>
              <div className="flex justify-end">
                <Button
                  size="sm"
                  onClick={handleReschedule}
                  disabled={savingResched || !reDate}
                  className="bg-purple-600 hover:bg-purple-700 h-8"
                  data-testid="task-quick-view-reschedule-save"
                >
                  {savingResched
                    ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                    : <CalendarClock className="h-3.5 w-3.5 mr-1" />}
                  Save new time
                </Button>
              </div>
            </div>
          )}
        </div>
        <DialogFooter className="gap-2">
          {!isCompleted && (
            <Button
              variant="outline"
              onClick={() => setRescheduling((v) => !v)}
              data-testid="task-quick-view-reschedule"
            >
              <CalendarClock className="h-3.5 w-3.5 mr-1" />
              {rescheduling ? 'Hide reschedule' : 'Reschedule'}
            </Button>
          )}
          <Button
            variant="outline"
            onClick={openDeal}
            data-testid="task-quick-view-open-deal"
          >
            <ExternalLink className="h-3.5 w-3.5 mr-1" /> Open Deal
          </Button>
          {!isCompleted && (
            <Button
              onClick={handleComplete}
              disabled={completing}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="task-quick-view-complete"
            >
              {completing
                ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                : <CheckCircle className="h-3.5 w-3.5 mr-1" />}
              Mark Complete
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TaskQuickViewModal;
